import clsx from 'clsx';
import { FC, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import styles from './about-page.module.scss';
import { IAboutPageProps } from './types';

import { Contacts } from '../../components';
import { Title, Paragraph, ETitleLevel, Loader } from '../../components/ui';
import logoGradient from '../../images/logo-gradient.png';

import { removeCurrentData, saveCurrentPaginationPage } from 'src/models';

import { DataContext, getAboutInfo, TGetAboutInfoTexts } from '~utils';

export const AboutPage: FC<IAboutPageProps> = ({ className = '', ...rest }) => {
  const [aboutText, setAboutText] = useState<TGetAboutInfoTexts | null>(null);
  const { contacts } = useContext(DataContext);

  const { t } = useTranslation();

  useEffect(() => {
    removeCurrentData();
    saveCurrentPaginationPage(1);

    getAboutInfo().then(res => setAboutText(res.data));
  }, []);

  if (!contacts) return <Loader />;

  return (
    <section className={clsx(styles.container, className)} {...rest}>
      <div className={styles.main_content}>
        <div className={styles.banner}>
          <div className={styles.banner_textbox}>
            <LazyLoadImage src={logoGradient} alt={t('alts.logo-alt') || ''} />
            <Paragraph className={styles.banner_text}>{t('about.banner-text')}</Paragraph>
          </div>
        </div>
        <div className={styles.about}>
          <Title level={ETitleLevel.h2} className={styles.title}>
            {t('about.about-title')}
          </Title>
          <ul className={styles.about_list}>
            <li className={styles.about_list_item}>
              <div className={styles.about_list_item_textbox}>
                <Paragraph isGradient className={styles.about_list_item_title}>
                  {t('about.about-title1')}
                </Paragraph>
                <Paragraph className={styles.about_list_item_text}>
                  {t('about.about-text1')}
                </Paragraph>
              </div>
            </li>
            <li className={styles.about_list_item}>
              <div className={styles.about_list_item_textbox}>
                <Paragraph isGradient className={styles.about_list_item_title}>
                  {t('about.about-title2')}
                </Paragraph>
                <Paragraph className={styles.about_list_item_text}>
                  {t('about.about-text2')}
                </Paragraph>
              </div>
            </li>
            <li className={styles.about_list_item}>
              <div className={styles.about_list_item_textbox}>
                <Paragraph isGradient className={styles.about_list_item_title}>
                  {t('about.about-title3')}
                </Paragraph>
                <Paragraph className={styles.about_list_item_text}>
                  {t('about.about-text3')}
                </Paragraph>
              </div>
            </li>
          </ul>
        </div>
        <div className={styles.columns}>
          <div className={styles.columns_column}>
            <Paragraph isGradient className={styles.columns_column_title}>
              {aboutText?.title ?? t('about.contacts-box-title')}
            </Paragraph>
            <Paragraph className={styles.columns_column_text}>
              {aboutText?.text ?? t('about.contacts-box-text')}
            </Paragraph>
          </div>
          <div className={styles.columns_column}>
            <Title className={styles.columns_column_title}>{t('about.contacts-title')}</Title>
            <Contacts data={contacts} className={styles.contacts} />
          </div>
        </div>

        <div>
          <Title level={ETitleLevel.h2} className={styles.title}>
            {t('about.map-title')}
          </Title>
          <iframe
            src={contacts.map_link}
            title='address'
            width='100%'
            height='375'
            style={{ border: 'none', maxWidth: 1226 }}
            loading='lazy'
          />
        </div>
      </div>
    </section>
  );
};
